/**
 * authVariantState.ts — login 変種の解決状態(P2-2 = host allowlist 解決基盤)
 *
 * 2026-09-16 P2-1 edge4(L1-06): useMemberAuth が adapter を dynamic import で variant 選択するための同期 getter。
 * 2026-09-24 P2-2: `GET /api/session/variant`(server host allowlist)を非ブロッキングで解決する基盤を新設。
 *
 * 2026-09-24 P2-3: 判定を adapter 選択へ接続し、consume 後の値を session 中固定する。
 * 2026-09-28 P2-6: authority を選ぶ一次 consumer は ensureAuthVariantConsumed() で bounded resolution を待つ。
 *   production host は同期 variant1、候補hostの timeout/error は variant1 fail-safe。TTL/logout等の後続同期readも同じ固定値を見る。
 *
 * ★安全掟:
 *   - 解決は全 error/timeout/parse 失敗/非200 で **variant1 fail-safe**。variant2 は server が明示 'variant2' と答えた時だけ。
 *   - production は host allowlist 非該当ゆえ endpoint=variant1 → 必ず variant1(既存 login 挙動 差分なし)。
 *   - Firebase を 1 切 import しない(この module 自体が config.individual[secondaryApp=#129 根因]を掴まない)。
 *   - session cache(module state)=SPA 再 nav で再 fetch しない・値も変えない(idempotent 解決)。
 *   - consume 固定は 1 page load 単位(module state)=reload で解除→boot kick が再解決(解決済みなら variant2 を consume)。
 */
export type AuthVariant = 'variant1' | 'variant2';

// variant 解決 endpoint の timeout(cold BFF でも boot を掴まないよう有限化・§3-c)。
const RESOLVE_TIMEOUT_MS = 1200;

let resolved: AuthVariant | undefined;
// ★初回 getResolvedAuthVariant() で確定し以降不変(mid-session tearing 封じ・P2-3 consumed lock)。
let consumed: AuthVariant | undefined;
let ready: Promise<AuthVariant> | undefined;

/**
 * consume 済み variant の同期 getter(TTL・logout等の後続処理が消費)。
 * 2026-09-24 P2-3: 初回 read 時の解決値を **consume して固定**する(以降 resolved が variant2 に化けても返さない)。
 *   ★consumed lock(Codex/Antigravity MAJOR 修正・道2): adapter選択→TTL→logout が session を跨いで単一値を見る
 *     =未解決 mount で variant1 開始 → 後着 variant2 で adapter すり替わり(401 強制 logout=tearing)を封じる。
 *   ★await を足さない同期 read=variant1 に latency 退行ゼロ(login 不可侵)。未解決=variant1 safe fallback(両社「設計通り」)。
 *   ★production は host allowlist 非該当ゆえ endpoint が variant1 → 必ず variant1(既存 login 挙動 byte-identical)。
 *   ★未解決候補hostで返す variant1 は transient であり、authority 選択には使用しない。
 */
export function getResolvedAuthVariant(): AuthVariant {
  // 2026-09-28 P2-6 DETERMINISTIC_ENGAGEMENT_REPAIR(05/00 裁定B): unresolved と explicit variant1 を分離。
  //   旧実装は `resolved ?? 'variant1'` で **unresolved を即 variant1 として consume/lock** し、初回 consumer の
  //   到達 timing で variant2 host が variant1 に落ちる race(engagement 非決定)を生んでいた。是正=下記3段。
  if (consumed !== undefined) return consumed;
  // A. production/legacy host = 同期的に variant1 確定(server 二重ロック)。resolver network を待たない=login latency 不変。
  if (typeof window !== 'undefined' && isDefinitelyProductionHost(window.location.hostname)) {
    consumed = 'variant1';
    return consumed;
  }
  // C/D. variant2 候補 host で resolution 済なら consume(server 明示 variant2、または timeout/error fail-safe variant1)=immutable。
  if (resolved !== undefined) {
    consumed = resolved;
    return consumed;
  }
  // B/E. variant2 候補 host で unresolved = **consume せず** transient variant1 を返す(early sync read が variant1 へ
  //   immutable lock する race を封じる)。確定 consume は ensureAuthVariantConsumed()(resolver 完了待ち)または
  //   resolution 完了後の本 getter 再 read で行う。loading 中の transient read は member effect(!member gate)に届かない。
  return 'variant1';
}

/**
 * 解決済み値の同期 peek(未解決=variant1)= getResolvedAuthVariant が返す値(P2-3 接続済)。
 * 未解決 / 到達不能 / timeout / parse 不能 は必ず variant1(fail-safe)。variant2 は server 明示時のみ。
 */
export function peekAuthVariant(): AuthVariant {
  return resolved ?? 'variant1';
}

/**
 * cosmetic lane(useTenantDesign/ShopContactBar の variant2 短絡)用の整合 peek(P2 chunk gating・敵対レビュー MINOR 是正):
 *   auth lane が consume 固定済みならその値を返す=同一ページ内で auth lane(consumed)と cosmetic lane(resolved)が
 *   別 variant を見る分裂(design default 化+bar 非表示だけ variant2 適用)を防ぐ。未 consume は resolved(未解決=variant1)。
 *   ★consume はしない。authority 選択は useMemberAuth / IndividualLoginPage が ensureAuthVariantConsumed() で行い、
 *     page load 単位で単一値を固定する。
 */
export function peekAlignedAuthVariant(): AuthVariant {
  return consumed ?? peekAuthVariant();
}

/**
 * 本番 host の同期判定(P2 chunk gating・★prewarm タイミング専用=variant 判定には一切使わない)。
 *   server classifyHost の production 種別(sessionApi.js:179-186: legacy web.app/firebaseapp.com/*.bento-cloud.app)の
 *   client 鏡像。用途は index.tsx の config.individual 並列 prewarm の即時発火判定のみ:
 *   - true(本番)= variant は必ず variant1(server 二重ロック)ゆえ、待たずに prewarm= 従来と同時期に構築開始(latency 不可侵)。
 *   - false(preview/localhost 等)= server 判定を待って variant1 の時だけ prewarm(variant2 は構築させない)。
 *   ★誤判定の安全性: false-negative(本番を非本番と誤る)は prewarm が server 応答分遅れるだけ(機能無害)。
 *     variant の決定は常に server 応答+fail-safe(このヘルパは読み込みタイミングにしか影響しない)。
 *   ★apex 'bento-cloud.app' は server classifyHost(endsWith('.bento-cloud.app')のみ=apex は unknown)の【意図的 superset】
 *     (敵対レビュー MINOR 注記): client が apex を本番扱いする間は、apex を VARIANT2_HOST_ALLOWLIST に登録するのは禁
 *     (登録すると即 prewarm が variant2 host で構築する)。現運用で apex が weborder を serve する実態は無い。
 */
export function isDefinitelyProductionHost(hostname: string): boolean {
  const h = (hostname || '').toLowerCase();
  return (
    h === 'greensoft-weborder.web.app' ||
    h === 'greensoft-weborder.firebaseapp.com' ||
    h === 'bento-cloud.app' ||
    h.endsWith('.bento-cloud.app')
  );
}

/**
 * login 変種を server(`GET /api/session/variant`)から非ブロッキングで解決する(fire-and-forget 可)。
 * idempotent(2回目以降は同じ promise)・session cache・全 error=variant1 fail-safe。
 */
export function kickAuthVariantResolution(): Promise<AuthVariant> {
  if (ready) return ready;
  ready = (async (): Promise<AuthVariant> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), RESOLVE_TIMEOUT_MS);
    try {
      const res = await fetch('/api/session/variant', {
        method: 'GET',
        headers: { 'X-GS-App': 'weborder' },
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (res.status !== 200) {
        resolved = 'variant1';
        return resolved;
      }
      const body = (await res.json().catch(() => null)) as { variant?: unknown } | null;
      const v = body && typeof body === 'object' ? body.variant : null;
      // ★variant2 は server が明示 'variant2' と答えた時だけ。それ以外(未知/欠落/型違い)は必ず variant1。
      resolved = v === 'variant2' ? 'variant2' : 'variant1';
      return resolved;
    } catch {
      // network / timeout / abort / parse = variant1(fail-safe)
      resolved = 'variant1';
      return resolved;
    } finally {
      clearTimeout(timer);
    }
  })();
  return ready;
}

/**
 * 解決 promise を返す(未 kick なら kick する)。boot kick の warm 完了待ち・テスト用。
 */
export function authVariantResolved(): Promise<AuthVariant> {
  return kickAuthVariantResolution();
}

/**
 * variant を **決定的に consume** する async 経路(2026-09-28 P2-6 DETERMINISTIC_ENGAGEMENT_REPAIR・05/00 裁定B)。
 *   一次 consumer(useMemberAuth の adapter 選択)が呼ぶ。consumer 到達 timing に依らず同じ variant を返す(§3-E)。
 *   ★A(production/legacy host)= 同期 variant1(resolver network 待ち 0=variant1 login latency 不変・§5)。
 *   ★B/C/D(variant2 候補 host)= resolver 完了(kickAuthVariantResolution 内 RESOLVE_TIMEOUT_MS で有限)を待って consume:
 *       server 明示 variant2 → variant2 / timeout・network error・invalid → variant1 fail-safe。いずれも **1回だけ consume=immutable**。
 *   ★consumed-lock 維持: 一度 consume したら以降不変(後着 variant で切替えない)。set-once ガードで並行 consume も単一値。
 *   ★全 login lane を async 化しない: authority確定前に外部経路を選ぶ一次 consumer(useMemberAuth / IndividualLoginPage)
 *     だけが await する。consume 後のTTL/logout等は同期 getResolvedAuthVariant()で同一値を見る。
 */
export function ensureAuthVariantConsumed(): Promise<AuthVariant> {
  if (consumed !== undefined) return Promise.resolve(consumed);
  if (typeof window !== 'undefined' && isDefinitelyProductionHost(window.location.hostname)) {
    consumed = 'variant1';
    return Promise.resolve(consumed);
  }
  return kickAuthVariantResolution().then((v) => {
    if (consumed === undefined) consumed = v; // set-once(並行 consume 競合の封じ・immutable)
    return consumed as AuthVariant;
  });
}

/** ★テスト専用: module state を初期化(本番コードから呼ばない)。 */
export function __resetAuthVariantStateForTest(): void {
  resolved = undefined;
  consumed = undefined;
  ready = undefined;
}
